import React, { useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-enterprise';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-balham.css';
import useGridOptionsManageDeliveryLocation from '../../../hooks/grid/gridOptions/useGridOptionsManageDeliveryLocation';
import commonColDef from '../../../data/grid/commonColDef';
import ActionsManageDeliveryLocation from '../../cellRenderers/ActionsManageDeliveryLocation';
import SteelMakerCellRenderer from '../../cellRenderers/SteelMakerCellRenderer';
import UserNameFromEmailCellRenderer from '../../cellRenderers/UserNameFromEmailCellRenderer';
import useColdefManageDeliveryLocation from '../../../hooks/grid/coldef/useColdefManageDeliveryLocation';
import { useTranslation } from 'react-i18next';
import useLocales from '../../../hooks/useLocales';
import useUserRoles from '../../../hooks/useUserRoles';
import { RootState, useDispatch, useSelector } from '../../../redux/store';
import { setDialogMode, openDialog, selectDeliveryLocation } from '../../../redux/slices/deliveryLocation';
import type { ColDef, ColumnApi, GridApi, GridReadyEvent, RowDoubleClickedEvent } from 'ag-grid-community';
import type { AgGridReact as AgGridReactType } from 'ag-grid-react/lib/agGridReact';
import type { DeliveryLocationType } from '../../../types/deliveryLocation';

const GridDeliveryLocation = React.forwardRef<AgGridReactType>((props, ref): React.ReactElement => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { isMaintenanceadmin } = useUserRoles();
  const { currentLang } = useLocales();
  const { steelMaker } = useSelector((state: RootState) => state.options);
  const { users } = useSelector((state: RootState) => state.user);
  const { isLoading, deliveryLocations } = useSelector((state: RootState) => state.deliveryLocation);
  const [gridApi, setGridApi] = useState<GridApi | undefined>(undefined);
  const [gridColumnApi, setGridColumnApi] = useState<ColumnApi | undefined>(undefined);
  const { gridOptions } = useGridOptionsManageDeliveryLocation();
  const customColDef: ColDef = {
    cellClass: 'cell',
    flex: 1,
  };
  const defaultColDef: ColDef = Object.assign({}, commonColDef, customColDef);
  const coldefManageDeliveryLocation = useColdefManageDeliveryLocation(t);
  const columnDefs: ColDef[] = coldefManageDeliveryLocation;
  const frameworkComponents = {
    actionsRenderer: ActionsManageDeliveryLocation,
    steelMakerCellRenderer: SteelMakerCellRenderer,
    userNameFromEmailCellRenderer: UserNameFromEmailCellRenderer,
  };

  // SIDE EFFECT
  // ================================================================================

  // 確認用
  // useEffect(() => {
  // }, []);

  /**
   * Loading, NoRowsのグリッド表示
   */
  useEffect(() => {
    if (gridApi) {
      if (isLoading) {
        gridApi.showLoadingOverlay();
      } else {
        if (deliveryLocations?.length) {
          gridApi.hideOverlay();
        } else {
          gridApi.showNoRowsOverlay();
        }
      }
    } else {
      // do nothing
    }
  }, [gridApi, isLoading, deliveryLocations]);

  useEffect(() => {
    if (gridApi && gridColumnApi) {
      const columnKeys: string[] = ['steelMaker', 'code', 'updatedAt', 'updatedBy', 'createdAt', 'createdBy'];
      gridColumnApi.autoSizeColumns(columnKeys);
      gridApi.redrawRows();
    }
  }, [gridColumnApi, deliveryLocations]);

  // EVENT HANDLER
  // ================================================================================

  /**
   * グリッドが準備できたらAPIを利用できるようにする
   * @param params GridReadyEvent
   */
  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
  };

  /**
   * ダブルクリックで編集モードにする
   * @param params GridReadyEvent
   */
  const onRowDoubleClicked = (event: RowDoubleClickedEvent) => {
    if (isMaintenanceadmin) {
      const data: DeliveryLocationType = event.data;
      dispatch(selectDeliveryLocation(data));
      dispatch(setDialogMode('UPDATE'));
      dispatch(openDialog());
    }
  };

  // VIEW
  // ================================================================================

  return (
    <div className="ag-theme-balham" style={{ height: '70vh' }}>
      <AgGridReact
        ref={ref}
        immutableData={true}
        gridOptions={gridOptions}
        defaultColDef={defaultColDef}
        columnDefs={columnDefs}
        rowData={deliveryLocations}
        onGridReady={onGridReady}
        onRowDoubleClicked={onRowDoubleClicked}
        frameworkComponents={frameworkComponents}
        // filterValueGetterで使うデータ
        context={{
          currentLang,
          // reduxにあるものはコピーを渡す
          steelMaker: steelMaker?.map((maker) => structuredClone(maker)),
          users: users?.map((user) => structuredClone(user)),
        }}
      />
    </div>
  );
});

// forwardRefの場合にはlinterに要求される場合がある
// https://stackoverflow.com/questions/67992894/component-definition-is-missing-display-name-for-forwardref
GridDeliveryLocation.displayName = 'GridDeliveryLocation';
export default GridDeliveryLocation;
